import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormBuilder } from '../utils/useFormBuilder';
import { createWorkOrderForm, updateWorkOrderForm, deleteWorkOrderForm } from '../api/work-order-formsAPI';
import { createFormData } from '../api/form-dataAPI';
import { createWorkOrderFormAction, deleteWorkOrderFormAction, getWorkOrderFormActionsByWorkOrderFormId } from '../api/work-order-form-actionsAPI';
import { getFormById } from '../api/formsAPI';
import { Icon } from '@iconify/react';
import { getPaginatedWorkOrders } from '../api/work-orderAPI';
import { getPaginatedAssets, getPaginatedAssetsAndAreas } from '../api/assetAPI';
import { getGooglePlaces } from '../api/google-mapsAPI';
import { getDepartmentChildren } from '../api/shift-logAPI';
import { getHierarchyStructureById } from '../api/hierarchy-structureAPI';
import { useValidator } from '../utils/useValidator';

const steps = ['Type', 'Assignees', 'Item', 'Assignment Details'];
const EXPIRY_OPTIONS = [1, 2, 3, 4, 5, 7, 14, 30].map((days) => ({ value: days, label: `${days} Days (${days})` }));

const toDateTimeValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (num) => String(num).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const getDurationHours = (startAt, dueDate) => {
  const start = startAt ? new Date(startAt).getTime() : NaN;
  const end = dueDate ? new Date(dueDate).getTime() : NaN;
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 0;
  return Number(((end - start) / (1000 * 60 * 60)).toFixed(2));
};

const getCurrentDefaultDateTime = () => toDateTimeValue(new Date());

const getDateTimeFromCalendarDay = (calendarDay) => {
  if (!calendarDay) return '';
  const now = new Date();
  const selected = new Date(`${calendarDay}T00:00:00`);
  if (Number.isNaN(selected.getTime())) return '';
  selected.setHours(now.getHours(), now.getMinutes(), 0, 0);
  return toDateTimeValue(selected);
};

const getDefaultStartAt = (workOrder, calendarDay) => {
  const calendarDateTime = getDateTimeFromCalendarDay(calendarDay);
  if (calendarDateTime) return calendarDateTime;

  const scheduledAtRaw = workOrder?.scheduledAt;
  if (scheduledAtRaw) {
    const scheduledAtDate = new Date(scheduledAtRaw);
    if (!Number.isNaN(scheduledAtDate.getTime())) {
      const now = new Date();
      if (scheduledAtDate < now) {
        scheduledAtDate.setFullYear(now.getFullYear(), now.getMonth(), now.getDate());
      }
      const adjustedValue = toDateTimeValue(scheduledAtDate);
      if (adjustedValue) return adjustedValue;
    }
  }

  const scheduledAtValue = toDateTimeValue(scheduledAtRaw);
  if (scheduledAtValue) return scheduledAtValue;
  return getCurrentDefaultDateTime();
};

const hasDepartmentAccess = (source, selected) => {
  if (!selected || selected.length === 0) return true;
  if (selected.includes(-1)) return true;
  const sourceList = Array.isArray(source) ? source : source ? [source] : [];
  return sourceList.some((id) => selected.includes(Number(id)));
};

const readFormAssignmentDepartmentIds = (form) => {
  const assignments = [
    ...(Array.isArray(form?.formAssignments) ? form.formAssignments : []),
    ...(Array.isArray(form?.assignments) ? form.assignments : []),
    ...(form?.formAssignment ? [form.formAssignment] : []),
    ...(form?.assignment ? [form.assignment] : []),
  ];
  const out = assignments.flatMap((assignment) => {
    const raw = assignment?.departmentIds;
    if (Array.isArray(raw)) return raw.map(Number).filter(Number.isFinite);
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed.map(Number).filter(Number.isFinite);
      } catch (_error) {
        return raw.split(',').map((v) => Number(v.trim())).filter(Number.isFinite);
      }
    }
    return [];
  });
  return [...new Set(out)];
};

const readItemDepartmentIds = (item) => {
  const raw = item?.departmentIds ?? item?.departmentId;
  if (Array.isArray(raw)) return raw.map(Number).filter(Number.isFinite);
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.map(Number).filter(Number.isFinite);
    } catch (_error) {
      return raw.split(',').map((v) => Number(v.trim())).filter(Number.isFinite);
    }
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? [parsed] : [];
};

const formatDepartmentDeliveryAddress = (department) => {
  if (!department) return '';
  const line = [department.deliveryAddressL1, department.deliveryAddressL2].filter(Boolean).join(' ').trim();
  const locality = [department.deliveryCity, department.deliveryState, department.deliveryPostCode].filter(Boolean).join(', ').trim();
  return [line, locality].filter(Boolean).join(', ').trim();
};

const formatAssetName = (asset) => {
  if (!asset) return null;
  const assetNumber = String(asset.assetNumber || asset.unitNumber || '').trim();
  const assetDescription = String(asset.assetDescription || '').trim();
  return [assetNumber, assetDescription].filter(Boolean).join(' ').trim() || null;
};

const WorkAssignDrawer = ({
  show,
  context,
  onClose,
  isSaving = false,
  crewOptions,
  contractorOptions,
  workOrderOptions,
  formOptions,
  selectedDepartmentIds,
  onConfirm,
  initialMode = 'workOrder',
}) => {
  const [step, setStep] = useState(0);
  const [kind, setKind] = useState(initialMode);
  const [selectedCrewIds, setSelectedCrewIds] = useState([]);
  const [selectedContractorIds, setSelectedContractorIds] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [selectedWorkOrderOption, setSelectedWorkOrderOption] = useState(null);
  const [selectedAssetOrAreaOption, setSelectedAssetOrAreaOption] = useState(null);
  const [workOrderFilterFormData, setWorkOrderFilterFormData] = useState({ departmentId: null });
  const [selectedAssetOption, setSelectedAssetOption] = useState(null);
  const [selectedDepartmentOption, setSelectedDepartmentOption] = useState(null);
  const [selectedDepartmentDetails, setSelectedDepartmentDetails] = useState(null);
  const [selectedDepartmentHierarchyLabel, setSelectedDepartmentHierarchyLabel] = useState('');
  const [selectedDepartmentWithChildrenIds, setSelectedDepartmentWithChildrenIds] = useState([]);
  const [isDepartmentLockedFromWorkOrder, setIsDepartmentLockedFromWorkOrder] = useState(false);
  const [isAssetLockedFromWorkOrder, setIsAssetLockedFromWorkOrder] = useState(false);
  const [searchedWorkOrdersById, setSearchedWorkOrdersById] = useState({});
  const [searchedAssetsById, setSearchedAssetsById] = useState({});
  const [selectedFormDetails, setSelectedFormDetails] = useState(null);
  const [details, setDetails] = useState({
    reportTo: '',
    jobExpiryDays: 5,
    startAt: '',
    duration: 0,
    meetingPoint: '',
    location: '',
    notes: '',
  });
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [isLoadingAddressSuggestions, setIsLoadingAddressSuggestions] = useState(false);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [communicationOptions, setCommunicationOptions] = useState({
    sms: { enabled: false, disabled: true },
    email: { enabled: false, disabled: true },
  });
  const [isCommunicationStateInitialized, setIsCommunicationStateInitialized] = useState(false);

  useEffect(() => {
    if (!show) return;
    const defaultKind = context?.kind || initialMode || 'workOrder';
    setStep(Number.isInteger(context?.initialStep) ? context.initialStep : 0);
    setKind(defaultKind);
    setSelectedCrewIds(context?.assignee?.type === 'internal' ? [context.assignee.id] : []);
    setSelectedContractorIds(context?.assignee?.type === 'contractor' ? [context.assignee.id] : []);
    setSelectedItemId(context?.workOrderNumber ?? null);
    setSelectedWorkOrderOption(null);
    setWorkOrderFilterFormData({ departmentId: null });
    setSelectedAssetOrAreaOption(null);
    setSelectedAssetOption(null);
    setSelectedDepartmentOption(null);
    setSelectedDepartmentDetails(null);
    setSelectedDepartmentHierarchyLabel('');
    setSelectedDepartmentWithChildrenIds([]);
    setIsDepartmentLockedFromWorkOrder(false);
    setIsAssetLockedFromWorkOrder(false);
    setSearchedWorkOrdersById({});
    setSearchedAssetsById({});
    setSelectedFormDetails(null);
    setDetails({
      reportTo: '',
      jobExpiryDays: 5,
      startAt: getDateTimeFromCalendarDay(context?.date) || getCurrentDefaultDateTime(),
      duration: 0,
      meetingPoint: '',
      location: '',
      notes: '',
    });
    setAddressSuggestions([]);
    setIsLoadingAddressSuggestions(false);
    setShowAddressSuggestions(false);
    setCommunicationOptions({
      sms: { enabled: false, disabled: true },
      email: { enabled: false, disabled: true },
    });
    setIsCommunicationStateInitialized(false);
  }, [show, context, initialMode]);

  const filteredForms = useMemo(() => formOptions
    .filter((form) => hasDepartmentAccess(readFormAssignmentDepartmentIds(form), selectedDepartmentIds))
    .slice(0, 100), [formOptions, selectedDepartmentIds]);

  const toWorkOrderOption = useCallback((wo) => ({
    value: wo.id,
    label: `${wo.workOrderNumber || ''} ${wo.workOrderTitle || `Work Order ${wo.id}`} | ${wo?.asset?.assetNumber || ''} ${wo?.asset?.assetDescription || ''}`.trim(),
  }), []);

  const selectedDepartmentFilterIds = useMemo(
    () => {
      const selected = workOrderFilterFormData?.departmentId;
      if (Array.isArray(selected)) {
        return selected.map((departmentId) => Number(departmentId)).filter(Number.isFinite);
      }
      const selectedId = Number(selected);
      return Number.isFinite(selectedId) ? [selectedId] : [];
    },
    [workOrderFilterFormData?.departmentId],
  );

  const selectedAssetOrAreaFilter = useMemo(() => {
    const selectedType = String(selectedAssetOrAreaOption?.type || '').toLowerCase();
    const selectedId = Number(selectedAssetOrAreaOption?.value);

    return {
      selectedType,
      selectedId,
      assetId: (selectedType.includes('asset') || !selectedType) && Number.isFinite(selectedId)
        ? selectedId
        : undefined,
      areaId: selectedType.includes('area') && Number.isFinite(selectedId)
        ? selectedId
        : undefined,
    };
  }, [selectedAssetOrAreaOption]);

  const loadWorkOrderOptions = useCallback(async (inputValue, { page = 1 } = {}) => {
    const selectedDepartments = selectedDepartmentFilterIds.length > 0 && selectedDepartmentFilterIds[0] !== 0
      ? selectedDepartmentFilterIds
      : null;

    const pageSize = 25;

    const response = await getPaginatedWorkOrders({
      page,
      pageSize,
      search: inputValue || '',
      departmentId: selectedDepartments,
      statusId: [1, 2],
      assetId: selectedAssetOrAreaFilter.assetId,
      areaId: selectedAssetOrAreaFilter.areaId,
    });

    const rows = Array.isArray(response?.data) ? response.data : [];
    const options = rows.map(toWorkOrderOption);

    setSearchedWorkOrdersById((prev) => {
      const next = { ...prev };
      rows.forEach((wo) => {
        if (wo?.id !== undefined && wo?.id !== null) {
          next[wo.id] = wo;
        }
      });
      return next;
    });

    const totalCount =
      typeof response?.totalCount === 'number'
        ? response.totalCount
        : typeof response?.total === 'number'
          ? response.total
          : typeof response?.count === 'number'
            ? response.count
            : undefined;

    const hasMore =
      typeof response?.hasMore === 'boolean'
        ? response.hasMore
        : typeof response?.has_more === 'boolean'
          ? response.has_more
          : typeof totalCount === 'number'
            ? page * pageSize < totalCount
            : rows.length === pageSize;

    return {
      options,
      hasMore,
      totalCount,
      pageSize,
    };
  }, [selectedDepartmentIds, selectedDepartmentFilterIds, selectedAssetOrAreaFilter, toWorkOrderOption]);

  const loadAssetOrAreaOptions = useCallback(async (inputValue, { page = 1 } = {}) => {
    const pageSize = 25;
    const selectedDepartments = selectedDepartmentFilterIds.length > 0
      ? selectedDepartmentFilterIds
      : (Array.isArray(selectedDepartmentIds) && !selectedDepartmentIds.includes(-1) ? selectedDepartmentIds : []);

    const response = await getPaginatedAssetsAndAreas({
      page,
      pageSize,
      search: inputValue || '',
      type: 'all',
      departmentId: selectedDepartments,
      assetId: selectedAssetOrAreaFilter.assetId,
      areaId: selectedAssetOrAreaFilter.areaId,
    });

    const rows = Array.isArray(response?.data) ? response.data : [];
    const filteredRows = selectedDepartmentFilterIds.length > 0
      ? rows.filter((item) => readItemDepartmentIds(item).some((departmentId) => selectedDepartmentFilterIds.includes(departmentId)))
      : rows;

    const options = filteredRows.map((item) => ({
      value: item.id,
      label: item.label,
      type: item.type,
      departmentId: item.departmentId,
    }));

    const totalCount =
      typeof response?.total === 'number'
        ? response.total
        : typeof response?.count === 'number'
          ? response.count
          : undefined;

    const hasMore =
      typeof response?.hasMore === 'boolean'
        ? response.hasMore
        : typeof totalCount === 'number'
          ? page * pageSize < totalCount
          : rows.length === pageSize;

    return {
      options,
      hasMore,
      totalCount,
      pageSize,
    };
  }, [selectedDepartmentFilterIds, selectedDepartmentIds, selectedAssetOrAreaFilter]);

  const workOrderFiltersRefreshKey = useMemo(() => JSON.stringify({
    departmentIds: selectedDepartmentFilterIds,
    assetOrAreaValue: selectedAssetOrAreaOption?.value ?? null,
    assetOrAreaType: selectedAssetOrAreaOption?.type ?? null,
  }), [selectedDepartmentFilterIds, selectedAssetOrAreaOption?.value, selectedAssetOrAreaOption?.type]);

  const selectedWorkOrder = useMemo(() => {
    const selectedId = Number(selectedItemId);
    if (!Number.isFinite(selectedId)) return null;
    return searchedWorkOrdersById[selectedId]
      || workOrderOptions.find((wo) => Number(wo.id) === selectedId)
      || null;
  }, [workOrderOptions, selectedItemId, searchedWorkOrdersById]);

  const selectedForm = useMemo(() => formOptions.find((f) => Number(f.id) === Number(selectedItemId)) || null, [formOptions, selectedItemId]);

  const selectedCrewMember = useMemo(() => {
    const selectedId = Number(selectedCrewIds?.[0]);
    if (!Number.isFinite(selectedId)) return null;
    return crewOptions.find((member) => Number(member?.id) === selectedId) || null;
  }, [crewOptions, selectedCrewIds]);

  const selectedContractor = useMemo(() => {
    const selectedId = Number(selectedContractorIds?.[0]);
    if (!Number.isFinite(selectedId)) return null;
    return contractorOptions.find((member) => Number(member?.id) === selectedId) || null;
  }, [contractorOptions, selectedContractorIds]);

  const communicationTarget = useMemo(() => {
    if (selectedContractor) return { type: 'contractor', person: selectedContractor };
    if (selectedCrewMember) return { type: 'user', person: selectedCrewMember };
    return null;
  }, [selectedContractor, selectedCrewMember]);

  const communicationPhone = useMemo(() => {
    const person = communicationTarget?.person;
    if (!person) return '';
    return String(
      person.phoneE164
      || person.phone
      || person.user_phone
      || person.phoneNumber
      || person.mobile
      || '',
    ).trim();
  }, [communicationTarget]);

  const communicationEmail = useMemo(() => {
    const person = communicationTarget?.person;
    if (!person) return '';
    return String(person.email || person.userEmail || '').trim();
  }, [communicationTarget]);

  useEffect(() => {
    const isContractor = communicationTarget?.type === 'contractor';
    const hasSms = Boolean(communicationPhone);
    const hasEmail = Boolean(communicationEmail);

    setCommunicationOptions((prev) => {
      const smsDisabled = !isContractor || !hasSms;
      const emailDisabled = !hasEmail;
      const shouldUseDefaults = !isCommunicationStateInitialized;
      const next = {
        sms: {
          enabled: shouldUseDefaults
            ? (!smsDisabled && isContractor)
            : (!smsDisabled && prev.sms.enabled),
          disabled: smsDisabled,
        },
        email: {
          enabled: shouldUseDefaults
            ? (!emailDisabled && isContractor)
            : (!emailDisabled && prev.email.enabled),
          disabled: emailDisabled,
        },
      };

      if (
        prev.sms.enabled === next.sms.enabled
        && prev.sms.disabled === next.sms.disabled
        && prev.email.enabled === next.email.enabled
        && prev.email.disabled === next.email.disabled
      ) {
        return prev;
      }

      return next;
    });
    if (!isCommunicationStateInitialized) {
      setIsCommunicationStateInitialized(true);
    }
  }, [communicationTarget, communicationPhone, communicationEmail, isCommunicationStateInitialized]);

  useEffect(() => {
    let isMounted = true;
    const loadSelectedFormDetails = async () => {
      if (kind !== 'form' || !selectedForm?.id) {
        setSelectedFormDetails(null);
        return;
      }

      try {
        const formDetails = await getFormById(selectedForm.id);
        if (isMounted) {
          setSelectedFormDetails(formDetails || null);
        }
      } catch (_error) {
        if (isMounted) {
          setSelectedFormDetails(null);
        }
      }
    };

    loadSelectedFormDetails();
    return () => {
      isMounted = false;
    };
  }, [kind, selectedForm?.id]);

  const selectedAsset = useMemo(() => {
    const selectedId = Number(details?.assetId);
    if (!Number.isFinite(selectedId)) return null;
    return searchedAssetsById[selectedId] || null;
  }, [details?.assetId, searchedAssetsById]);

  const selectedDepartmentDisplay = useMemo(() => {
    if (selectedDepartmentHierarchyLabel) return selectedDepartmentHierarchyLabel;
    if (selectedDepartmentDetails?.name) return selectedDepartmentDetails.name;
    if (selectedDepartmentOption?.label) return selectedDepartmentOption.label;
    if (selectedDepartmentOption?.value) return `Department ${selectedDepartmentOption.value}`;
    return '';
  }, [selectedDepartmentDetails?.name, selectedDepartmentHierarchyLabel, selectedDepartmentOption]);

  const selectedAssetDisplay = useMemo(() => {
    if (kind === 'workOrder') {
      const selectedType = String(selectedAssetOrAreaOption?.type || '').toLowerCase();
      if (selectedType.includes('area')) return selectedAssetOrAreaOption?.label || '';
      if (selectedAssetOrAreaOption?.label) return selectedAssetOrAreaOption.label;
    }
    const resolvedAssetLabel = formatAssetName(selectedAsset);
    if (resolvedAssetLabel) return resolvedAssetLabel;
    return String(selectedAssetOption?.label || '').replace(/\s-\s/g, ' ').trim();
  }, [kind, selectedAsset, selectedAssetOption?.label, selectedAssetOrAreaOption]);

  const formHasAssetDetailsCell = useMemo(() => {
    const items = selectedFormDetails?.tblFormItems || selectedFormDetails?.items || [];
    return items.some((item) => Number(item?.formInputTypeId || item?.inputTypeID) === 15);
  }, [selectedFormDetails]);

  useEffect(() => {
    if (kind !== 'workOrder') {
      setSelectedWorkOrderOption(null);
      return;
    }

    if (!selectedWorkOrder) {
      if (!selectedItemId) {
        setSelectedWorkOrderOption(null);
      }
      return;
    }

    setSelectedWorkOrderOption((prev) => {
      if (prev?.value === selectedWorkOrder.id) return prev;
      return toWorkOrderOption(selectedWorkOrder);
    });
  }, [kind, selectedWorkOrder, selectedItemId, toWorkOrderOption]);

  useEffect(() => {
    if (kind !== 'workOrder' || !selectedWorkOrder) {
      setIsDepartmentLockedFromWorkOrder(false);
      setIsAssetLockedFromWorkOrder(false);
      return;
    }

    const departmentId = Number(selectedWorkOrder?.departmentId);
    const assetId = Number(selectedWorkOrder?.assetId);
    const areaId = Number(selectedWorkOrder?.areaId);

    if (Number.isFinite(departmentId)) {
      setSelectedDepartmentOption({ value: departmentId, label: selectedWorkOrder?.department?.name || `Department ${departmentId}` });
      setWorkOrderFilterFormData((prev) => ({ ...prev, departmentId }));
      setIsDepartmentLockedFromWorkOrder(true);
    } else {
      setIsDepartmentLockedFromWorkOrder(false);
    }

    if (Number.isFinite(assetId)) {
      setSelectedAssetOrAreaOption({
        value: assetId,
        type: 'asset',
        label: `${selectedWorkOrder?.asset?.assetNumber || ''} ${selectedWorkOrder?.asset?.assetDescription || ''}`.trim() || `Asset ${assetId}`,
      });
      setIsAssetLockedFromWorkOrder(true);
    } else if (Number.isFinite(areaId)) {
      setSelectedAssetOrAreaOption({
        value: areaId,
        type: 'area',
        label: selectedWorkOrder?.area?.name || `Area ${areaId}`,
      });
      setIsAssetLockedFromWorkOrder(true);
    } else {
      setIsAssetLockedFromWorkOrder(false);
    }
  }, [kind, selectedWorkOrder]);

  useEffect(() => {
    if (kind === 'workOrder' && selectedWorkOrder) {
      setDetails((prev) => ({
        ...prev,
        startAt: getDefaultStartAt(selectedWorkOrder, context?.date),
        duration: getDurationHours(selectedWorkOrder?.scheduledAt, selectedWorkOrder?.dueDate),
        meetingPoint: selectedWorkOrder?.meetingPoint || '',
        location: selectedWorkOrder?.locationAddress || formatDepartmentDeliveryAddress(selectedDepartmentDetails) || '',
      }));
    }
  }, [kind, selectedWorkOrder, context?.date, selectedDepartmentDetails]);

  useEffect(() => {
    let isMounted = true;
    const departmentId = Number(selectedDepartmentOption?.value);

    const loadDepartmentContext = async () => {
      if (!Number.isFinite(departmentId)) {
        setSelectedDepartmentDetails(null);
        setSelectedDepartmentHierarchyLabel('');
        setSelectedDepartmentWithChildrenIds([]);
        return;
      }

      try {
        const [department, descendants] = await Promise.all([
          getHierarchyStructureById(departmentId),
          getDepartmentChildren(departmentId),
        ]);
        if (!isMounted) return;

        const hierarchyParts = [];
        const visited = new Set();
        let currentDepartment = department;

        while (currentDepartment) {
          const currentId = Number(currentDepartment?.id);
          if (!Number.isFinite(currentId) || visited.has(currentId)) {
            break;
          }

          visited.add(currentId);
          if (currentDepartment?.name) {
            hierarchyParts.unshift(currentDepartment.name);
          }

          const parentId = Number(currentDepartment?.parentId);
          if (!Number.isFinite(parentId)) {
            break;
          }

          // eslint-disable-next-line no-await-in-loop
          currentDepartment = await getHierarchyStructureById(parentId);
        }

        const childIds = Array.isArray(descendants)
          ? descendants
            .map((row) => Number(row?.id ?? row))
            .filter(Number.isFinite)
          : [];

        setSelectedDepartmentDetails(department || null);
        setSelectedDepartmentHierarchyLabel(hierarchyParts.join(' > '));
        setSelectedDepartmentWithChildrenIds(Array.from(new Set([departmentId, ...childIds])));
      } catch (_error) {
        if (!isMounted) return;
        setSelectedDepartmentDetails(null);
        setSelectedDepartmentHierarchyLabel(selectedDepartmentOption?.label || '');
        setSelectedDepartmentWithChildrenIds([departmentId]);
      }
    };

    loadDepartmentContext();
    return () => {
      isMounted = false;
    };
  }, [selectedDepartmentOption]);

  useEffect(() => {
    const departmentAddress = formatDepartmentDeliveryAddress(selectedDepartmentDetails);
    if (!departmentAddress) return;

    if (kind === 'form' && selectedDepartmentOption?.value) {
      setDetails((prev) => (prev.location ? prev : { ...prev, location: departmentAddress }));
      return;
    }

    if (kind === 'workOrder' && selectedWorkOrder && !selectedWorkOrder?.locationAddress) {
      setDetails((prev) => (prev.location ? prev : { ...prev, location: departmentAddress }));
    }
  }, [kind, selectedDepartmentDetails, selectedDepartmentOption?.value, selectedWorkOrder]);

  const toAssetOption = useCallback((asset) => ({
    value: asset.id,
    label: `${asset.assetNumber || asset.unitNumber || ''} - ${asset.assetDescription || ''}`.trim(),
  }), []);

  const loadAssetOptions = useCallback(async (inputValue, { page = 1 } = {}) => {
    const pageSize = 25;
    const response = await getPaginatedAssets({
      page,
      pageSize,
      search: inputValue || '',
      assetDepartmentId: selectedDepartmentWithChildrenIds.length > 0 ? selectedDepartmentWithChildrenIds : undefined,
    });

    const rows = Array.isArray(response?.data) ? response.data : [];
    const assetsById = {};
    rows.forEach((asset) => {
      if (asset?.id !== undefined && asset?.id !== null) {
        assetsById[Number(asset.id)] = asset;
      }
    });
    if (Object.keys(assetsById).length > 0) {
      setSearchedAssetsById((prev) => ({ ...prev, ...assetsById }));
    }

    const options = rows.map(toAssetOption);
    const totalCount = typeof response?.totalCount === 'number'
      ? response.totalCount
      : typeof response?.count === 'number'
        ? response.count
        : undefined;
    const hasMore = typeof totalCount === 'number'
      ? page * pageSize < totalCount
      : rows.length === pageSize;

    return {
      options,
      hasMore,
      totalCount,
      pageSize,
    };
  }, [selectedDepartmentWithChildrenIds, toAssetOption]);

  useEffect(() => {
    let isMounted = true;
    const trimmedSearch = (details?.location || '').trim();

    if (step !== 3 || trimmedSearch.length < 3) {
      setAddressSuggestions([]);
      setIsLoadingAddressSuggestions(false);
      return;
    }

    const timer = setTimeout(async () => {
      setIsLoadingAddressSuggestions(true);
      try {
        const data = await getGooglePlaces(trimmedSearch, 5);
        if (isMounted && Array.isArray(data)) {
          setAddressSuggestions(data);
        }
      } catch (_error) {
        if (isMounted) {
          setAddressSuggestions([]);
        }
      } finally {
        if (isMounted) {
          setIsLoadingAddressSuggestions(false);
        }
      }
    }, 300);

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [details?.location, step]);

  const itemFieldConfig = useMemo(() => [{
    field: 'selectedItemId',
    label: kind === 'workOrder' ? 'Work Order' : 'Form',
    type: kind === 'workOrder' ? 'paginated-select' : 'select',
    required: true,
    size: 12,
    options: kind === 'form'
      ? filteredForms.map((form) => ({ value: form.id, label: form.name || `Form ${form.id}` }))
      : [],
    loadOptions: kind === 'workOrder' ? loadWorkOrderOptions : undefined,
    selectedOption: kind === 'workOrder' ? selectedWorkOrderOption : undefined,
    setSelectedOption: kind === 'workOrder' ? setSelectedWorkOrderOption : undefined,
    minSearchLength: 0,
  }], [kind, filteredForms, loadWorkOrderOptions, selectedWorkOrderOption]);

  const detailsFieldConfig = useMemo(() => ([
    { field: 'reportTo', label: 'Reporting To', type: 'text', size: 6 },
    { field: 'jobExpiryDays', label: 'Job Expiry', type: 'select', options: EXPIRY_OPTIONS, size: 6, required: true },
    { field: 'startAt', label: 'Start Time', type: 'datetime', size: 6, required: true },
    { field: 'duration', label: 'Estimated Duration (Hours)', type: 'number', size: 6, min: 0, step: '0.01' },
    { field: 'meetingPoint', label: 'Meeting Point', type: 'text', size: 12, maxLength: 100 },
  ]), []);

  const step1Fields = useMemo(() => ([
    { field: 'selectedItemId', label: kind === 'workOrder' ? 'Work Order' : 'Form', type: 'text', required: true },
    ...(kind === 'form' ? [{ field: 'departmentId', label: 'Department', type: 'text', required: true }] : []),
  ]), [kind]);

  const step2Fields = useMemo(() => ([
    { field: 'selectedAssignees', label: 'Assignees', type: 'text', required: true },
  ]), []);


  const validationFields = useMemo(() => ([
    { field: 'startAt', label: 'Scheduled At', type: 'datetime', required: true },
    { field: 'jobExpiryDays', label: 'Job Expiry', type: 'number', required: true, mustBePositiveInteger: true },
  ]), []);

  const { errors: validationErrors, validate } = useValidator(validationFields, details);
  const step1ValidationState = useMemo(() => ({
    selectedItemId,
    departmentId: selectedDepartmentOption?.value || null,
  }), [selectedItemId, selectedDepartmentOption?.value]);
  const { errors: step1Errors, validate: validateStep1 } = useValidator(step1Fields, step1ValidationState);
  const step2ValidationState = useMemo(() => ({ selectedAssignees: [...selectedCrewIds, ...selectedContractorIds] }), [selectedCrewIds, selectedContractorIds]);
  const { errors: step2Errors, validate: validateStep2 } = useValidator(step2Fields, step2ValidationState);

  const assetFieldConfig = useMemo(() => ([{
    field: 'assetId',
    label: 'Asset (Optional)',
    type: 'paginated-select',
    size: 12,
    required: false,
    loadOptions: loadAssetOptions,
    selectedOption: selectedAssetOption,
    setSelectedOption: setSelectedAssetOption,
    minSearchLength: 0,
    disabled: kind === 'workOrder' && isAssetLockedFromWorkOrder,
  }]), [kind, isAssetLockedFromWorkOrder, loadAssetOptions, selectedAssetOption]);

  const departmentFieldConfig = useMemo(() => ([{
    field: 'departmentId',
    label: 'Department',
    type: 'select',
    size: 12,
    required: true,
    api: 'hierarchy-structures',
    displayField: 'name',
    isMulti: false,
    selectedOption: selectedDepartmentOption,
    setSelectedOption: setSelectedDepartmentOption,
    disabled: kind === 'workOrder' && isDepartmentLockedFromWorkOrder,
  }]), [kind, isDepartmentLockedFromWorkOrder, selectedDepartmentOption]);

  const itemFieldErrors = useMemo(() => ({
    ...(step1Errors?.selectedItemId ? { selectedItemId: `Please select a ${kind === 'workOrder' ? 'work order' : 'form'}` } : {}),
    ...(step1Errors?.departmentId ? { departmentId: 'Please select a Department' } : {}),
  }), [step1Errors, kind]);

  const { renderField: renderItemField } = useFormBuilder(itemFieldConfig, { selectedItemId }, (setter) => {
    if (typeof setter === 'function') {
      const next = setter({ selectedItemId });
      setSelectedItemId(next.selectedItemId ?? null);
      return;
    }
    setSelectedItemId(setter.selectedItemId ?? null);
  }, itemFieldErrors, { refreshKey: kind === 'workOrder' ? workOrderFiltersRefreshKey : `kind:${kind}` });

  const workOrderFilterFieldConfig = useMemo(() => ([
    {
      field: 'departmentId',
      label: 'Department (Optional)',
      type: 'select',
      size: 12,
      api: 'hierarchy-structures',
      displayField: 'name',
      isMulti: false,
      isClearable: true,
      disabled: isDepartmentLockedFromWorkOrder,
    },
    {
      field: 'assetOrArea',
      label: 'Asset or Area (Optional)',
      type: 'paginated-select',
      size: 12,
      required: false,
      loadOptions: loadAssetOrAreaOptions,
      selectedOption: selectedAssetOrAreaOption,
      setSelectedOption: setSelectedAssetOrAreaOption,
      minSearchLength: 0,
      isClearable: true,
      disabled: isAssetLockedFromWorkOrder,
    },
  ]), [isAssetLockedFromWorkOrder, isDepartmentLockedFromWorkOrder, loadAssetOrAreaOptions, selectedAssetOrAreaOption]);

  const { renderField: renderWorkOrderFilterField } = useFormBuilder(
    workOrderFilterFieldConfig,
    workOrderFilterFormData,
    setWorkOrderFilterFormData,
    {},
    { refreshKey: workOrderFiltersRefreshKey },
  );

  const { renderField: renderDetailsField } = useFormBuilder(detailsFieldConfig, details, setDetails, validationErrors);
  const { renderField: renderAssetField } = useFormBuilder(assetFieldConfig, details, setDetails, {});
  const { renderField: renderDepartmentField } = useFormBuilder(
    departmentFieldConfig,
    { departmentId: selectedDepartmentOption?.value || null },
    (setter) => {
      if (kind === 'workOrder' && isDepartmentLockedFromWorkOrder) return;
      const nextValue = typeof setter === 'function'
        ? setter({ departmentId: selectedDepartmentOption?.value || null })?.departmentId
        : setter?.departmentId;

      if (!nextValue) {
        setSelectedDepartmentOption(null);
        setSelectedDepartmentHierarchyLabel('');
        if (kind === 'form') {
          setSelectedAssetOption(null);
          setDetails((prev) => ({ ...prev, assetId: null }));
        }
        return;
      }
      setSelectedDepartmentOption((prev) => (prev?.value === Number(nextValue) ? prev : { value: Number(nextValue), label: prev?.label || `Department ${nextValue}` }));
    },
    itemFieldErrors,
  );

  useEffect(() => {
    if (!step1Errors?.selectedItemId && !step1Errors?.departmentId) return;
    validateStep1();
  }, [selectedItemId, selectedDepartmentOption?.value, step1Errors?.selectedItemId, step1Errors?.departmentId, validateStep1]);

  useEffect(() => {
    if (kind !== 'workOrder') return;
    if (!selectedAssetOrAreaOption) return;
    const selectedDepartmentId = Number(selectedAssetOrAreaOption?.departmentId);
    if (!Number.isFinite(selectedDepartmentId)) return;
    if (selectedDepartmentFilterIds.length === 0) return;
    if (selectedDepartmentFilterIds.includes(selectedDepartmentId)) return;
    setSelectedAssetOrAreaOption(null);
  }, [kind, selectedAssetOrAreaOption, selectedDepartmentFilterIds]);

  useEffect(() => {
    if (kind !== 'form') return;
    setSelectedDepartmentOption(null);
    setSelectedDepartmentDetails(null);
    setSelectedDepartmentHierarchyLabel('');
    setSelectedDepartmentWithChildrenIds([]);
    setSelectedAssetOption(null);
    setDetails((prev) => ({ ...prev, assetId: null }));
  }, [kind, selectedItemId]);

  useEffect(() => {
    if (!formHasAssetDetailsCell) {
      setSelectedAssetOption(null);
      setDetails((prev) => (prev.assetId ? { ...prev, assetId: null } : prev));
      return;
    }

    if (!selectedAsset) return;
    setSelectedAssetOption((prev) => {
      if (prev?.value === Number(selectedAsset.id)) return prev;
      return toAssetOption(selectedAsset);
    });
  }, [formHasAssetDetailsCell, selectedAsset, toAssetOption]);

  const toggleCrew = (crewId) => {
    if (kind === 'form') {
      const already = selectedCrewIds.includes(crewId);
      setSelectedCrewIds(already ? [] : [crewId]);
      if (!already) setSelectedContractorIds([]);
      return;
    }
    setSelectedCrewIds((prev) => (prev.includes(crewId) ? prev.filter((id) => id !== crewId) : [...prev, crewId]));
  };

  const toggleContractor = (contractorId) => {
    if (kind === 'form') {
      const already = selectedContractorIds.includes(contractorId);
      setSelectedContractorIds(already ? [] : [contractorId]);
      if (!already) setSelectedCrewIds([]);
      return;
    }
    setSelectedContractorIds((prev) => (prev.includes(contractorId) ? prev.filter((id) => id !== contractorId) : [...prev, contractorId]));
  };

  const handleConfirm = async () => {
    if (isSaving) return;
    if (!validate()) return;
    const assignmentDetails = {
      globalContractorId: details.contractorId,
      reportTo: details.reportTo || '',
      reportingTo: details.reportTo || '',
      startAt: details.startAt,
      duration: Number(details.duration || 0),
      expiryDate: new Date(Date.now() + details.jobExpiryDays * 86400000).toISOString(),
      meetingPoint: details.meetingPoint || '',
      location: details.location || '',
      locationAddress: details.location || '',
      instructions: details.notes || '',
    }

    const departmentId = Number(selectedDepartmentOption?.value);
    const departmentName = selectedDepartmentDetails?.name || selectedDepartmentOption?.label || null;
    const selectedAssetFromWorkOrder = kind === 'workOrder'
      ? (selectedWorkOrder?.asset || (Number.isFinite(Number(selectedWorkOrder?.assetId))
        ? {
          id: Number(selectedWorkOrder?.assetId),
          assetNumber: selectedWorkOrder?.asset?.assetNumber,
          assetDescription: selectedWorkOrder?.asset?.assetDescription,
        }
        : null))
      : null;
    const resolvedAsset = selectedAssetOption || selectedAssetFromWorkOrder;
    const resolvedAssetId = Number(
      resolvedAsset?.id
      || selectedWorkOrder?.assetId
      || selectedAssetOption?.value
      || (String(selectedAssetOrAreaOption?.type || '').toLowerCase().includes('asset') ? selectedAssetOrAreaOption?.value : null),
    );
    const resolvedAssetName = kind === 'workOrder'
      ? (String(selectedAssetOrAreaOption?.label 
        || '').trim() 
        || formatAssetName(resolvedAsset))
      : resolvedAsset?.label;


    const payload = {
      kind,
      selectedItemId: Number(selectedItemId),
      crewIds: selectedCrewIds,
      contractorIds: selectedContractorIds,
      details,
      selectedWorkOrder: selectedWorkOrder || null,
      selectedForm: selectedForm || null,
      assignmentDetails,
      selectedAsset: resolvedAsset || null,
      selectedDepartment: Number.isFinite(departmentId)
        ? { id: departmentId, name: departmentName, ...selectedDepartmentDetails }
        : null,
      selectedDepartmentAddress: formatDepartmentDeliveryAddress(selectedDepartmentDetails),
      selectedAssetId: Number.isFinite(resolvedAssetId) ? resolvedAssetId : null,
      selectedAssetName: resolvedAssetName || null,
      selectedFormDetails: selectedFormDetails || null,
      communicationOptions: {
        type: communicationTarget?.type || null,
        phone: communicationPhone || null,
        email: communicationEmail || null,
        smsEnabled: Boolean(communicationOptions.sms.enabled && !communicationOptions.sms.disabled),
        emailEnabled: Boolean(communicationOptions.email.enabled && !communicationOptions.email.disabled),
      },
    };
    await onConfirm?.(payload);
  };

  if (!show) return null;

  return (
    <div className='modal d-block' tabIndex='-1' role='dialog' aria-modal='true'>
      <div className='modal-dialog modal-lg modal-dialog-scrollable'>
        <div className='modal-content'>
          <div className='modal-header'>
            <h5 className='modal-title'>Assign Work</h5>
            <button type='button' className='btn-close' onClick={onClose} disabled={isSaving} />
          </div>
          <div className='modal-body d-flex flex-column gap-3' style={{ maxHeight: "calc(100vh - 200px", minHeight: "650px" }}>
            {step === 0 && (
              <>
                <div className="text-md fw-semibold">Select the type of document to assign</div>
                <div className="w-100 d-flex justify-content-center gap-3 my-48">
                  <button
                    type='button'
                    disabled={Boolean(context?.lockKind)}
                    className={`btn p-3 mx-36 ${kind === 'workOrder' ? 'btn-primary' : 'btn-outline-primary'}`}
                    style={{ height: "150px", width: "200px" }}
                    onClick={() => {
                      setKind('workOrder');
                      setSelectedItemId(null);
                    }}
                  >
                    <div className="d-flex flex-column align-items-center justify-content-center h-100 text-lg">
                      <Icon icon="mdi:clipboard-text-outline" className="mb-2" style={{ fontSize: "32px" }} />
                      Work Order
                    </div>
                  </button>

                  <button
                    type='button'
                    disabled={Boolean(context?.lockKind)}
                    className={`btn p-3  mx-36 ${kind === 'form' ? 'btn-primary' : 'btn-outline-primary'}`}
                    style={{ height: "150px", width: "200px" }}
                    onClick={() => {
                      setKind('form');
                      setSelectedItemId(null);
                    }}
                  >
                    <div className="d-flex flex-column align-items-center justify-content-center h-100 text-lg">
                      <Icon icon="mdi:clipboard-edit-outline" className="mb-2" style={{ fontSize: "32px" }} />
                      Form
                    </div>
                  </button>
                </div>
              </>
            )}

            {step === 1 && (
              <div className='row g-2'>
                {kind === 'workOrder' && workOrderFilterFieldConfig.map((field) => (
                  <React.Fragment key={field.field}>{renderWorkOrderFilterField(field)}</React.Fragment>
                ))}
                {itemFieldConfig.map((field) => <React.Fragment key={field.field}>{renderItemField(field)}</React.Fragment>)}
                {kind === 'form' && selectedItemId && departmentFieldConfig.map((field) => (
                  <React.Fragment key={field.field}>{renderDepartmentField(field)}</React.Fragment>
                ))}
                {kind === 'form' && selectedItemId && selectedDepartmentOption?.value && formHasAssetDetailsCell && assetFieldConfig.map((field) => (
                  <React.Fragment key={field.field}>{renderAssetField(field)}</React.Fragment>
                ))}
              </div>
            )}

            {step === 2 && (
              <div className='row g-3'>
                <div className="text-md fw-semibold">Select the Crew Members and Contractors</div>
                <div className='col-md-12 mb-16'>
                  <div
                    className="px-16 py-8 mb-8 bg-light fw-semibold"
                    style={{
                      borderWidth: 1,
                      borderStyle: "solid",
                      borderRadius: "8px",
                      borderColor: "#ddd",
                    }}>
                    Crew</div>
                  <div className='list-group overflow-auto' style={{ maxHeight: '250px' }}>
                    {crewOptions.map((crew) => (
                      <label key={crew.id} className='list-group-item d-flex align-items-center gap-2'>
                        <input type='checkbox' className='form-check-input mt-0' checked={selectedCrewIds.includes(crew.id)} disabled={kind === 'form' && selectedContractorIds.length > 0 && !selectedCrewIds.includes(crew.id)} onChange={() => toggleCrew(crew.id)} />
                        {crew.name}
                      </label>
                    ))}
                  </div>
                </div>
                <div className='col-md-12 mb-16'>
                  <div
                    className="px-16 py-8 mb-8 bg-light fw-semibold"
                    style={{
                      borderWidth: 1,
                      borderStyle: "solid",
                      borderRadius: "8px",
                      borderColor: "#ddd",
                    }}>
                    Contractors
                  </div>
                  <div className='list-group overflow-auto' style={{ maxHeight: '250px' }}>
                    {contractorOptions.map((contractor) => (
                      <label key={contractor.id} className='list-group-item d-flex align-items-center gap-2'>
                        <input type='checkbox' className='form-check-input mt-0' checked={selectedContractorIds.includes(contractor.id)} disabled={kind === 'form' && selectedCrewIds.length > 0 && !selectedContractorIds.includes(contractor.id)} onChange={() => toggleContractor(contractor.id)} />
                        {contractor.name}{contractor.supplierName ? ` (${contractor.supplierName})` : ''}
                      </label>
                    ))}
                  </div>
                  {step2Errors?.selectedAssignees ? <div className='invalid-feedback d-block'>Please select at least one crew member or contractor</div> : null}
                </div>
              </div>
            )}
            {step === 3 && (
              <div className='row g-2'>
                {selectedDepartmentDisplay && (
                  <div className='col-md-12'>
                    <label className='form-label'>Department</label>
                    <input type='text' className='form-control' value={selectedDepartmentDisplay} disabled />
                  </div>
                )}
                {selectedAssetDisplay && (
                  <div className='col-md-12'>
                    <label className='form-label'>{kind === 'workOrder' ? 'Asset or Area' : 'Asset'}</label>
                    <input type='text' className='form-control' value={selectedAssetDisplay} disabled />
                  </div>
                )}
                {detailsFieldConfig.map((field) => (
                  <React.Fragment key={field.field}>{renderDetailsField(field)}</React.Fragment>
                ))}

                <div className='col-12 position-relative'>
                  <label className='form-label'>Location Address</label>
                  <input
                    type='text'
                    className='form-control'
                    value={details.location || ''}
                    placeholder='Search address / location'
                    onFocus={() => setShowAddressSuggestions(true)}
                    onBlur={() => {
                      setTimeout(() => setShowAddressSuggestions(false), 150);
                    }}
                    onChange={(e) => {
                      const value = e.target.value;
                      setDetails((prev) => ({ ...prev, location: value }));
                      setShowAddressSuggestions(true);
                    }}
                  />
                  {showAddressSuggestions && ((details.location || '').trim().length >= 3) && (
                    <div
                      className='border rounded mt-1 bg-white position-absolute start-0 end-0 shadow-sm overflow-auto'
                      style={{ zIndex: 20, maxHeight: '220px' }}
                    >
                      {isLoadingAddressSuggestions && (
                        <div className='px-3 py-2 text-muted small'>Searching addresses...</div>
                      )}
                      {!isLoadingAddressSuggestions && addressSuggestions.length === 0 && (
                        <div className='px-3 py-2 text-muted small'>No address matches found.</div>
                      )}
                      {!isLoadingAddressSuggestions &&
                        addressSuggestions.map((suggestion, index) => {
                          const label = suggestion?.description || suggestion?.formattedAddress || suggestion?.name || '';
                          if (!label) return null;

                          return (
                            <button
                              key={`${label}-${index}`}
                              type='button'
                              className='btn btn-link text-start w-100 text-decoration-none px-3 py-2 border-bottom rounded-0'
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => {
                                setDetails((prev) => ({ ...prev, location: label }));
                                setShowAddressSuggestions(false);
                              }}
                            >
                              {label}
                            </button>
                          );
                        })}
                    </div>
                  )}
                </div>

                <div className='col-12'>
                  <label className='form-label'>Notes</label>
                  <textarea
                    className='form-control'
                    rows={3}
                    value={details.notes || ''}
                    onChange={(e) => setDetails((prev) => ({ ...prev, notes: e.target.value }))}
                  />
                </div>

                <div className='col-12 mt-3'>
                  {(() => {
                    const enabledChannels = [
                      communicationTarget?.type === 'contractor' && communicationOptions.sms.enabled ? 'SMS' : null,
                      communicationOptions.email.enabled ? 'Email' : null,
                    ].filter(Boolean);

                    const notificationSummary =
                      enabledChannels.length > 0 ? enabledChannels.join(' and ') : 'No notifications';

                    return (
                      <div className='border rounded-3 p-3 bg-primary-50 mt-8'>
                        <div className='d-flex flex-column gap-1 mb-3'>
                          <div className='fw-semibold'>Send Notifications</div>
                          <div className='small text-muted'>
                            Saving this job will send it via <span className='fw-medium text-dark'>{notificationSummary}</span>.
                          </div>
                        </div>

                        {communicationTarget?.type === 'contractor' && (
                          <div className='d-flex align-items-center justify-content-between rounded p-3 bg-white border mb-16'>
                            <div className='d-flex flex-column pe-3'>
                              <span className='fw-medium'>SMS</span>
                              <span className='text-muted small'>
                                {communicationPhone || 'No phone number recorded'}
                              </span>
                            </div>

                            <div className='form-switch switch-primary d-flex mb-0'>
                              <input
                                type='checkbox'
                                className='form-check-input'
                                role='switch'
                                checked={communicationOptions.sms.enabled}
                                disabled={communicationOptions.sms.disabled}
                                onChange={(event) => {
                                  setCommunicationOptions((prev) => ({
                                    ...prev,
                                    sms: { ...prev.sms, enabled: event.target.checked },
                                  }));
                                  setIsCommunicationStateInitialized(true);
                                }}
                              />
                            </div>
                          </div>
                        )}

                        <div className='d-flex align-items-center justify-content-between rounded p-3 bg-white border'>
                          <div className='d-flex flex-column pe-3'>
                            <span className='fw-medium'>Email</span>
                            <span className='text-muted small'>
                              {communicationEmail || 'No email address recorded'}
                            </span>
                          </div>

                          <div className='form-switch switch-primary d-flex mb-0'>
                            <input
                              type='checkbox'
                              className='form-check-input'
                              role='switch'
                              checked={communicationOptions.email.enabled}
                              disabled={communicationOptions.email.disabled}
                              onChange={(event) => {
                                setCommunicationOptions((prev) => ({
                                  ...prev,
                                  email: { ...prev.email, enabled: event.target.checked },
                                }));
                                setIsCommunicationStateInitialized(true);
                              }}
                            />
                          </div>
                        </div>

                        <div className='small text-muted mt-3'>
                          Choose how the contractor or technician should be notified when you save this form.
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <div className='mt-auto d-flex justify-content-between'>
              <button
                type='button'
                className='btn btn-outline-secondary'
                disabled={step === 0 || isSaving}
                onClick={() => setStep((prev) => Math.max(0, prev - 1))}>
                <Icon icon='mdi:chevron-left' className='me-8 text-xl' style={{ position: "relative", top: "-2px" }} />
                <span className="me-8">Back</span>
              </button>
              {step < steps.length - 1 ? (
                <button
                  type='button'
                  className='btn btn-primary ms-12'
                  disabled={isSaving}
                  onClick={() => {
                    if (step === 1 && !validateStep1()) {
                      return;
                    }
                    if (step === 2 && !validateStep2()) {
                      return;
                    }
                    if (step === 3 && !validate()) {
                      return;
                    }
                    setStep((prev) => Math.min(steps.length - 1, prev + 1));
                  }}>
                  <span className="ms-8">Next</span>
                  <Icon icon='mdi:chevron-right' className='ms-8 text-xl' style={{ position: "relative", top: "-2px" }} />
                </button>
              ) : (
                <button
                  type='button'
                  className='btn btn-primary ms-12 d-inline-flex align-items-center gap-2'
                  style={{ paddingLeft: "29px", paddingRight: "28px" }}
                  onClick={handleConfirm}
                  disabled={isSaving}
                >
                  {isSaving && <span className='spinner-border spinner-border-sm' />}
                  Save
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkAssignDrawer;
